import {
  IconButton,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'
import { ClearIcon } from '@mui/x-date-pickers'
import { useAppController } from 'customHooks/useAppController'
import { useCallback, useEffect, useState } from 'react'
import { DeliveryTermsDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useShowException } from 'utils/useShowException'
import { DeliveryTermsController } from './DeliveryTermsController'

type DeliveryTermsSelectorProps = TextFieldProps & {
  selectedDeliveryTermCode: string | null
  onDeliveryTermSelected?: (deliveryTermCode: string) => void
  onCleanDeliveryTerms: () => void
  label?: string
  labelClassName?: string
}

export const DeliveryTermsSelector = (props: DeliveryTermsSelectorProps) => {
  const { controller } = useAppController(() => new DeliveryTermsController())

  const {
    selectedDeliveryTermCode,
    onDeliveryTermSelected,
    onCleanDeliveryTerms,
    label,
    ...textFieldProps
  } = props

  const ShowException = useShowException('delivery terms')

  const [deliveryTerms, setDeliveryTerms] = useState<DeliveryTermsDto[]>([])

  const getDeliveryTerms = useCallback(async () => {
    try {
      const resp = await controller.getSelectableDeliveryTerms()
      setDeliveryTerms(resp.records)
    } catch (err) {
      ShowException(err)
    }
  }, [ShowException, controller])

  useEffect(() => {
    getDeliveryTerms()
  }, [getDeliveryTerms])

  if (!deliveryTerms.length) return null

  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          className={props.labelClassName}
          fontWeight={'bold'}
        >
          {label}
        </Typography>
      )}
      <TextField
        select
        onChange={(e) => onDeliveryTermSelected?.(e.target.value)}
        value={selectedDeliveryTermCode || ''}
        fullWidth
        slotProps={{
          input: {
            sx: {
              color: selectedDeliveryTermCode ? 'textPrimary' : 'GrayText',
            },
          },
          select: {
            displayEmpty: true,
          },
        }}
        {...textFieldProps}
      >
        <MenuItem value="" disabled>
          {props.placeholder}
        </MenuItem>
        {deliveryTerms.map((term) => (
          <MenuItem key={term.code} value={term.code}>
            {`${term.code} - ${term.description}`}
          </MenuItem>
        ))}
      </TextField>
      {selectedDeliveryTermCode && (
        <IconButton
          size="small"
          onClick={onCleanDeliveryTerms}
          sx={{
            position: 'absolute',
            right: '2rem',
            bottom: '1px',
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  )
}
